// Dummy Jacket data + in-memory store for mock mode (VITE_USE_MOCK=1).
// Mirrors the jacket list + new/edit form. Replaced by /api/V1/Jacket/* in
// Phase 3. Upload / OCR / PDF viewing are deferred (need S3 + Textract).

import type {
  AgentInfo,
  CplPrefill,
  Endorsement,
  FormType,
  JacketDetail,
  JacketFormTypes,
  JacketFormValues,
  JacketSummary,
  LenderSuggestion,
  PolicyType,
  ReadStatus,
  SaveResult,
} from '@/types/api';
import { OK_COUNTIES } from '@/lib/reference';

const sleep = <T,>(ms: number, value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

export const POLICY_TYPES: PolicyType[] = [
  { name: 'LM', label: 'Loan Policy' },
  { name: 'LO', label: "Owner's Policy" },
  { name: 'LM & LO', label: 'Simultaneous (Loan & Owner)' },
  { name: 'CO', label: 'Commitment' },
  { name: 'EN', label: 'Endorsement Only' },
];

export const PROPERTY_TYPES: string[] = [
  'Residential', 'Commercial', 'Vacant Land', 'Condominium', 'Agricultural', 'Multi-Family',
];

export const JACKET_FORM_TYPES: JacketFormTypes = {
  LM: [
    { id: 1, label: 'ALTA Loan Policy (06-17-06)' },
    { id: 2, label: 'ALTA Short Form Loan Policy' },
  ],
  LO: [
    { id: 3, label: "ALTA Owner's Policy (06-17-06)" },
    { id: 4, label: "ALTA Homeowner's Policy" },
  ],
  CO: [
    { id: 5, label: 'ALTA Commitment (2021)' },
  ],
};

export const ENDORSEMENTS: Endorsement[] = [
  { id: 1, name: 'ALTA 9 — Restrictions, Encroachments, Minerals' },
  { id: 2, name: 'ALTA 8.1 — Environmental Protection Lien' },
  { id: 3, name: 'ALTA 4 — Condominium' },
  { id: 4, name: 'ALTA 5 — Planned Unit Development' },
  { id: 5, name: 'ALTA 6 — Variable Rate Mortgage' },
  { id: 6, name: 'ALTA 22 — Location' },
  { id: 111, name: 'Other' },
];

const AGENT: AgentInfo = {
  name: 'Sam User', agency: 'American Security Title',
  address: '1234 N Broadway Ave, Suite 200', city: 'Oklahoma City', state: 'OK', zip: '73103',
};

function emptyLoan() {
  return { effectiveDate: '', formTypeId: 1, liabilityAmount: 0, overLimitApproval: false,
    grossPremium: 0, netPremium: 0, lenderName: '', borrowerName: '', scheduleB: '', loanNumber: '' };
}
function emptyOwner() {
  return { effectiveDate: '', formTypeId: 3, liabilityAmount: 0, overLimitApproval: false, grossPremium: 0, netPremium: 0 };
}
function emptyCommitment() {
  return { effectiveDate: '', formTypeId: 5, liabilityAmount: 0, grossPremium: 0 };
}

function docTypeFor(p: JacketDetail): string {
  switch (p.policyType) {
    case 'LM': return 'Loan Policy';
    case 'LO': return "Owner's Policy";
    case 'LM & LO': return 'Loan & Owner';
    case 'CO': return 'Commitment';
    case 'EN': return 'Endorsement';
    default: return '—';
  }
}
function effectiveDateFor(p: JacketDetail): string {
  return p.loan.effectiveDate || p.owner.effectiveDate || p.commitment.effectiveDate || '';
}
function liabilityFor(p: JacketDetail): number {
  return p.loan.liabilityAmount || p.owner.liabilityAmount || p.commitment.liabilityAmount || 0;
}

let nextId = 8000;
const READ: ReadStatus[] = ['success', 'processing', 'na', 'error'];

function make(
  id: number, file: string, jacketNo: string, policyType: JacketDetail['policyType'],
  date: string, amount: number, opts: Partial<JacketDetail> = {},
): JacketDetail {
  const base: JacketDetail = {
    id, fileNumber: file, policyType, propertyType: 'Residential',
    address: '100 Example St', city: 'Oklahoma City', county: 'Oklahoma', state: 'OK', zip: '73103',
    loan: emptyLoan(), owner: emptyOwner(), commitment: emptyCommitment(),
    mortgages: [], endorsements: [], void: false, agentName: AGENT.name, ...opts,
  };
  if (policyType === 'LM' || policyType === 'LM & LO') {
    base.loan = { ...base.loan, effectiveDate: date, liabilityAmount: amount, grossPremium: Math.round(amount * 0.0045),
      netPremium: Math.round(amount * 0.0036), lenderName: 'First Heartland Mortgage', borrowerName: 'Sample Borrower', loanNumber: 'LN-' + id };
  }
  if (policyType === 'LO' || policyType === 'LM & LO' || policyType === 'EN') {
    base.owner = { ...base.owner, effectiveDate: date, liabilityAmount: amount, grossPremium: Math.round(amount * 0.005), netPremium: Math.round(amount * 0.004) };
  }
  if (policyType === 'CO') {
    base.commitment = { ...base.commitment, effectiveDate: date, liabilityAmount: amount, grossPremium: Math.round(amount * 0.003) };
  }
  return base;
}

const STORE: JacketDetail[] = [
  make(7901, 'AST-26-04412', 'LP-100245', 'LM', '2026-06-02', 312000),
  make(7902, 'AST-26-04398', 'OP-100231', 'LO', '2026-05-28', 415000),
  make(7903, 'AST-26-04377', 'SM-100210', 'LM & LO', '2026-05-21', 525000),
  make(7904, 'AST-26-04361', 'CM-100199', 'CO', '2026-05-19', 280000),
  make(7905, 'AST-26-04340', 'LP-100188', 'LM', '2026-05-12', 198500),
  make(7906, 'AST-26-04318', 'OP-100174', 'LO', '2026-05-06', 1050000),
  make(7907, 'AST-26-04290', 'LP-100160', 'LM', '2026-04-29', 367250),
  make(7908, 'AST-26-04265', 'SM-100151', 'LM & LO', '2026-04-22', 612000),
  make(7909, 'AST-26-04231', 'CM-100143', 'CO', '2026-04-15', 240000),
  make(7910, 'AST-26-04205', 'OP-100129', 'LO', '2026-04-08', 489000, { void: true }),
];

function toSummary(p: JacketDetail, i: number): JacketSummary {
  return {
    id: p.id,
    readStatus: READ[i % READ.length],
    fileNumber: p.fileNumber,
    jacketNumber: p.id.toString().startsWith('7') ? p.fileNumber.replace('AST-26-', 'JK-') : 'JK-' + p.id,
    effectiveDate: effectiveDateFor(p),
    documentType: docTypeFor(p),
    liabilityAmount: liabilityFor(p),
    void: p.void,
    agentName: p.agentName,
  };
}

export const jacketMockApi = {
  async getPolicyTypes(): Promise<PolicyType[]> { return sleep(100, POLICY_TYPES); },
  async getJacketPropertyTypes(): Promise<string[]> { return sleep(80, PROPERTY_TYPES); },
  async getJacketFormTypes(): Promise<JacketFormTypes> { return sleep(100, JACKET_FORM_TYPES); },
  async getJacketEndorsements(): Promise<Endorsement[]> { return sleep(100, ENDORSEMENTS); },
  async getJacketCounties(): Promise<string[]> { return sleep(60, OK_COUNTIES); },
  async getJacketAgent(): Promise<AgentInfo> { return sleep(80, AGENT); },
  async getLenders(term: string): Promise<LenderSuggestion[]> {
    const all: LenderSuggestion[] = [
      { id: 1, label: 'First Heartland Mortgage', desc: 'Oklahoma City, OK', add: '100 Main St', city: 'Oklahoma City', state: 'OK', zip: '73102' },
      { id: 2, label: 'First National Bank', desc: 'Tulsa, OK', add: '200 Boston Ave', city: 'Tulsa', state: 'OK', zip: '74103' },
      { id: 3, label: 'Quicken Loans', desc: 'Detroit, MI', add: '1050 Woodward Ave', city: 'Detroit', state: 'MI', zip: '48226' },
      { id: 4, label: 'Bank of America', desc: 'Charlotte, NC', add: '100 N Tryon St', city: 'Charlotte', state: 'NC', zip: '28255' },
      { id: 5, label: 'Wells Fargo Home Mortgage', desc: 'Des Moines, IA', add: '800 Walnut St', city: 'Des Moines', state: 'IA', zip: '50309' },
    ];
    const t = term.trim().toLowerCase();
    return sleep(120, all.filter((l) => l.label.toLowerCase().startsWith(t)).slice(0, 20));
  },
  async getJacketPropertyByFile(fileNumber: string): Promise<CplPrefill | null> {
    if (!fileNumber.trim()) return sleep(80, null);
    return sleep(150, {
      address: '123 Mock Ave', city: 'Oklahoma City', county: 'Oklahoma',
      state: 'OK', zip: '73102', buyer: 'Sample Buyer', lender: 'First Heartland Mortgage',
    });
  },
  async getJacketPremium(amount: number): Promise<number> {
    // Mock approximation of the OCA rate (UI dev only).
    if (amount <= 0) return sleep(60, 0);
    const prem = amount <= 30000
      ? 134
      : amount <= 430000
        ? Math.round(134 + ((amount - 30000) / 1000) * 2.8)
        : Math.round(1190 + ((amount - 430000) / 1000) * 2);
    return sleep(80, prem);
  },

  async listJackets(params: { from?: string; to?: string; filter?: string; over300k?: boolean } = {}): Promise<JacketSummary[]> {
    const filter = (params.filter ?? '').trim().toLowerCase();
    const rows = STORE
      .filter((p) => (params.from ? effectiveDateFor(p) >= params.from : true))
      .filter((p) => (params.to ? effectiveDateFor(p) <= params.to : true))
      .filter((p) => (params.over300k ? liabilityFor(p) > 300000 : true))
      .filter((p) =>
        filter
          ? p.fileNumber.toLowerCase().includes(filter) || (p.agentName ?? '').toLowerCase().includes(filter)
          : true,
      )
      .sort((a, b) => effectiveDateFor(b).localeCompare(effectiveDateFor(a)))
      .map(toSummary);
    return sleep(250, rows);
  },

  async getJacket(id: number): Promise<JacketDetail | null> {
    const m = STORE.find((p) => p.id === id) ?? null;
    return sleep(180, m ? structuredClone(m) : null);
  },

  async createJacket(values: JacketFormValues): Promise<SaveResult> {
    const record: JacketDetail = { ...values, id: ++nextId, void: false, agentName: AGENT.name };
    STORE.unshift(record);
    return sleep(300, { err: '1', id: record.id });
  },

  async updateJacket(id: number, values: JacketFormValues): Promise<SaveResult> {
    const idx = STORE.findIndex((p) => p.id === id);
    if (idx === -1) return sleep(200, { err: '-4', message: 'Jacket not found.' });
    STORE[idx] = { ...STORE[idx], ...values };
    return sleep(300, { err: '1', id });
  },

  async voidJacket(id: number, reason: string): Promise<SaveResult> {
    void reason;
    const m = STORE.find((p) => p.id === id);
    if (m) m.void = true;
    return sleep(200, { err: '1', id });
  },
};
